import type * as React from 'react';
import { StyleSheet, View } from 'react-native';

import {
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    type BlockListItemData,
} from '@gitbook/doc-core';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';

import { sizes, spacing } from '../../constants';
import { DOMText, DOMView } from '../../DragAndDrop';
import { CheckBox } from '../../Form';
import { themedFonts, useCurrentTheme } from '../../theme';
import { usePlainMarkdownStyle } from '../usePlainMarkdownStyle';
import { BasicBlockWrapper } from './BasicBlockWrapper';

/*
 * Basic renderer for a list item block.
 */
export function BasicBlockListItem(props: BlockProps): React.ReactElement {
    const { node, parent, children, editor } = props;
    const { readOnly } = editor;

    const [, font] = useCurrentTheme(themedFonts);
    const { textStyle } = usePlainMarkdownStyle();

    const data = node.data.toFrozenObject() as BlockListItemData;
    const isUL = parent.type === BLOCK_LIST_UL;
    const isOL = parent.type === BLOCK_LIST_OL;
    const isTask = parent.type === BLOCK_LIST_TASKS;

    return (
        <BasicBlockWrapper tag="li" contentStyleProps={{ style: styles.blockListItem }} {...props}>
            {isTask && (
                <DOMView editable={false} style={[styles.blockListItemPrefix]}>
                    <CheckBox
                        value={!!data.checked}
                        onValueChange={() => {}}
                        readOnly={readOnly}
                        size={sizes.ICON_400}
                    />
                </DOMView>
            )}
            {isUL && (
                <DOMView editable={false} style={[styles.blockListItemPrefix]}>
                    <Icon.Dot size={12} style={textStyle} />
                </DOMView>
            )}
            {isOL && (
                <DOMText
                    editable={false}
                    style={[styles.blockListItemPrefix, font.Body, textStyle]}
                >
                    {parent.getChildIndex(node.key) + 1}.
                </DOMText>
            )}

            <View style={styles.blockListItemContent}>{children}</View>
        </BasicBlockWrapper>
    );
}

const styles = StyleSheet.create({
    blockListItem: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'baseline',
    },
    blockListItemPrefix: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: spacing.small,
        paddingRight: spacing.small,
    },
    blockListItemContent: {
        flex: 1,
    },
});
